import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { mockData } from '../mock';
import { apiService } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useLanguage } from '../contexts/LanguageContext';
import { qubitTranslations } from '../translations/qubit-translations';
import LoadingSpinner from './LoadingSpinner';

const Hero = () => {
  const { hero } = mockData;
  const [stats, setStats] = useState(mockData.stats);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const { success, error } = useToast();
  const { language } = useLanguage();
  const t = qubitTranslations[language];
  
  useEffect(() => {
    // Load real statistics from backend
    const loadStats = async () => {
      try {
        const realStats = await apiService.getStats();
        const formattedStats = [
          { number: `${realStats.students_trained}+`, label: t.studentsTrained },
          { number: `${realStats.course_modules}`, label: t.courseModules },
          { number: `${realStats.practical_lessons}+`, label: t.practicalLessons },
          { number: realStats.cost_savings, label: t.costSavings }
        ];
        setStats(formattedStats);
      } catch (err) {
        console.log('Using default stats:', err.message);
        // Keep default stats on error with translations
        const defaultStats = [
          { number: "500+", label: t.studentsTrained },
          { number: "8", label: t.courseModules },
          { number: "40+", label: t.practicalLessons },
          { number: "70%", label: t.costSavings }
        ];
        setStats(defaultStats);
      }
    };
    
    loadStats();
  }, [language, t]);

  const handleEnrollClick = async () => {
    setIsEnrolling(true);
    
    // Scroll to contact section for enrollment
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
    
    setTimeout(() => setIsEnrolling(false), 1000);
  };

  const handleViewCurriculum = () => {
    const curriculumSection = document.getElementById('curriculum');
    if (curriculumSection) {
      curriculumSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <section className="hero-section">
      <div className="hero-content">
        <div className="hero-announcement">
          <img 
            src="/qubit-logo.svg" 
            alt="Qubit Center" 
            className="w-4 h-4"
            style={{ filter: 'brightness(0) saturate(100%)' }}
          />
          {t.practicalCourse}
        </div>
        
        <h1 className="heading-hero hero-title">
          {t.heroTitle}
        </h1>
        
        <p className="body-large hero-subtitle">
          {t.heroSubtitle}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
          <button 
            onClick={handleEnrollClick}
            disabled={isEnrolling}
            className="btn-primary hover-scale relative"
          >
            {isEnrolling ? (
              <>
                <LoadingSpinner size="small" className="mr-2" />
                Loading...
              </>
            ) : (
              <>
                {t.enrollNow}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </button>
          
          <button 
            onClick={handleViewCurriculum}
            className="btn-secondary hover-scale"
          >
            {t.viewCurriculum}
          </button>
        </div>
        
        {/* Course Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 max-w-2xl mx-auto">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="heading-1 mb-2">{stat.number}</div>
              <div className="caption">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;